import { ExamViolationType, HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '../../models';
import { LocDisplayType } from '../../models/display-type';
import { SYSTEM_ENUM_ROOT, SYSTEM_ENUM_TYPE } from '../shared-data';

const dpRoot = `${SYSTEM_ENUM_ROOT}|Exam Violation Type|`;

type SharedKeys = Pick<LocDef, 'portal' | 'displayType' | 'htmlAllowed' | 'shared' | 'feature'>;

const sharedLocData: Pick<LocDef, 'displayType' | 'htmlAllowed' | 'shared' | 'feature'> = {
	displayType: `${SYSTEM_ENUM_TYPE}` as LocDisplayType,
	htmlAllowed: false,
	shared: true,
	feature: HubFeature.ExamSecurity,
};

const sharedSpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'SP',
};

const sharedCpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'CP',
};

export type CPExamViolationTypeLocs =
	| 'CPExamViolationTypeEnumAdvancedSecurityRule'
	| 'CPExamViolationTypeEnumCandidateRelated'
	| 'CPExamViolationTypeEnumCollusion'
	| 'CPExamViolationTypeEnumDifferentialPerformance'
	| 'CPExamViolationTypeEnumFailThenFail'
	| 'CPExamViolationTypeEnumFailThenPass'
	| 'CPExamViolationTypeEnumItemExposure'
	| 'CPExamViolationTypeEnumNoViolation'
	| 'CPExamViolationTypeEnumNull'
	| 'CPExamViolationTypeEnumOrder'
	| 'CPExamViolationTypeEnumPassThenFail'
	| 'CPExamViolationTypeEnumPassThenPass'
	| 'CPExamViolationTypeEnumPoorPerformance'
	| 'CPExamViolationTypeEnumPsychometric'
	| 'CPExamViolationTypeEnumRsi'
	| 'CPExamViolationTypeEnumScoreChange'
	| 'CPExamViolationTypeEnumScoreTooHighTimeTooShort'
	| 'CPExamViolationTypeEnumScoredUnscoredPerformance'
	| 'CPExamViolationTypeEnumTimeTooShort'
	| 'CPExamViolationTypeEnumTimeTooShortForTooManyItems'
	| 'CPExamViolationTypeEnumTotalExceeded'
	| 'CPExamViolationTypeEnumTrojanItem'
	| 'CPExamViolationTypeEnumVoucher'
	| 'CPExamViolationTypeEnumFaceRecognition';

export type SPExamViolationTypeLocs =
	| 'SPExamViolationTypeEnumAdvancedSecurityRule'
	| 'SPExamViolationTypeEnumCandidateRelated'
	| 'SPExamViolationTypeEnumCollusion'
	| 'SPExamViolationTypeEnumDifferentialPerformance'
	| 'SPExamViolationTypeEnumFailThenFail'
	| 'SPExamViolationTypeEnumFailThenPass'
	| 'SPExamViolationTypeEnumItemExposure'
	| 'SPExamViolationTypeEnumNoViolation'
	| 'SPExamViolationTypeEnumNull'
	| 'SPExamViolationTypeEnumOrder'
	| 'SPExamViolationTypeEnumPassThenFail'
	| 'SPExamViolationTypeEnumPassThenPass'
	| 'SPExamViolationTypeEnumPoorPerformance'
	| 'SPExamViolationTypeEnumPsychometric'
	| 'SPExamViolationTypeEnumRsi'
	| 'SPExamViolationTypeEnumScoreChange'
	| 'SPExamViolationTypeEnumScoreTooHighTimeTooShort'
	| 'SPExamViolationTypeEnumScoredUnscoredPerformance'
	| 'SPExamViolationTypeEnumTimeTooShort'
	| 'SPExamViolationTypeEnumTimeTooShortForTooManyItems'
	| 'SPExamViolationTypeEnumTotalExceeded'
	| 'SPExamViolationTypeEnumTrojanItem'
	| 'SPExamViolationTypeEnumVoucher'
	| 'SPExamViolationTypeEnumFaceRecognition';

export const cpExamViolationTypeEnumLocs: LocDefs<CPExamViolationTypeLocs> = {
	CPExamViolationTypeEnumAdvancedSecurityRule: {
		defaultValue: 'Advanced Security Rule',
		displayPath: `${dpRoot}Advanced Security Rule`,
		description: `System value for 'Advanced Security Rule' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumCandidateRelated: {
		defaultValue: 'Candidate Related',
		displayPath: `${dpRoot}Candidate Related`,
		description: `System value for 'Candidate Related' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumCollusion: {
		defaultValue: 'Collusion',
		displayPath: `${dpRoot}Collusion`,
		description: `System value for 'Collusion' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumDifferentialPerformance: {
		defaultValue: 'Differential Performance',
		displayPath: `${dpRoot}Differential Performance`,
		description: `System value for 'Differential Performance' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumFailThenFail: {
		defaultValue: 'Fail Then Fail',
		displayPath: `${dpRoot}Fail Then Fail`,
		description: `System value for 'Fail Then Fail' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumFailThenPass: {
		defaultValue: 'Fail Then Pass',
		displayPath: `${dpRoot}Fail Then Pass`,
		description: `System value for 'Fail Then Pass' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumItemExposure: {
		defaultValue: 'Item Exposure',
		displayPath: `${dpRoot}Item Exposure`,
		description: `System value for 'Item Exposure' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumNoViolation: {
		defaultValue: 'No Violation',
		displayPath: `${dpRoot}No Violation`,
		description: `System value for 'No Violation' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumNull: {
		defaultValue: 'Null',
		displayPath: `${dpRoot}Null`,
		description: `System value for 'Null' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumOrder: {
		defaultValue: 'Order',
		displayPath: `${dpRoot}Order`,
		description: `System value for 'Order' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumPassThenFail: {
		defaultValue: 'Pass Then Fail',
		displayPath: `${dpRoot}Pass Then Fail`,
		description: `System value for 'Pass Then Fail' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumPassThenPass: {
		defaultValue: 'Pass Then Pass',
		displayPath: `${dpRoot}Pass Then Pass`,
		description: `System value for 'Pass Then Pass' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumPoorPerformance: {
		defaultValue: 'Poor Performance',
		displayPath: `${dpRoot}Poor Performance`,
		description: `System value for 'Poor Performance' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumPsychometric: {
		defaultValue: 'Psychometric',
		displayPath: `${dpRoot}Psychometric`,
		description: `System value for 'Psychometric' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumRsi: {
		defaultValue: 'RSI',
		displayPath: `${dpRoot}RSI`,
		description: `System value for 'RSI' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumScoreChange: {
		defaultValue: 'Score Change',
		displayPath: `${dpRoot}Score Change`,
		description: `System value for 'Score Change' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumScoreTooHighTimeTooShort: {
		defaultValue: 'Score Too High Time Too Short',
		displayPath: `${dpRoot}Score Too High Time Too Short`,
		description: `System value for 'Score Too High Time Too Short' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumScoredUnscoredPerformance: {
		defaultValue: 'Scored Unscored Performance',
		displayPath: `${dpRoot}Scored Unscored Performance`,
		description: `System value for 'Scored Unscored Performance' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumTimeTooShort: {
		defaultValue: 'Time Too Short',
		displayPath: `${dpRoot}Time Too Short`,
		description: `System value for 'Time Too Short' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumTimeTooShortForTooManyItems: {
		defaultValue: 'Time Too Short For Too Many Items',
		displayPath: `${dpRoot}Time Too Short For Too Many Items`,
		description: `System value for 'Time Too Short For Too Many Items' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumTotalExceeded: {
		defaultValue: 'Total Exceeded',
		displayPath: `${dpRoot}Total Exceeded`,
		description: `System value for 'Total Exceeded' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumTrojanItem: {
		defaultValue: 'Trojan Item',
		displayPath: `${dpRoot}Trojan Item`,
		description: `System value for 'Trojan Item' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumVoucher: {
		defaultValue: 'Voucher',
		displayPath: `${dpRoot}Voucher`,
		description: `System value for 'Voucher' exam violation type`,
		...sharedCpLocData,
	},
	CPExamViolationTypeEnumFaceRecognition: {
		defaultValue: 'Face Recognition',
		displayPath: `${dpRoot}Face Recognition`,
		description: `System value for 'Face Recognition' exam violation type`,
		...sharedCpLocData,
	},
};

export const spExamViolationTypeEnumLocs: LocDefs<SPExamViolationTypeLocs> = {
	SPExamViolationTypeEnumAdvancedSecurityRule: {
		defaultValue: 'Advanced Security Rule',
		displayPath: `${dpRoot}Advanced Security Rule`,
		description: `System value for 'Advanced Security Rule' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.AdvancedSecurityRule',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumCandidateRelated: {
		defaultValue: 'Candidate Related',
		displayPath: `${dpRoot}Candidate Related`,
		description: `System value for 'Candidate Related' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.CandidateRelated',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumCollusion: {
		defaultValue: 'Collusion',
		displayPath: `${dpRoot}Collusion`,
		description: `System value for 'Collusion' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.Collusion',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumVoucher: {
		defaultValue: 'Voucher',
		displayPath: `${dpRoot}Voucher`,
		description: `System value for 'Voucher' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.Voucher',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumDifferentialPerformance: {
		defaultValue: 'Differential Performance',
		displayPath: `${dpRoot}Differential Performance`,
		description: `System value for 'Differential Performance' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.DifferentialPerformance',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumFailThenFail: {
		defaultValue: 'Fail Then Fail',
		displayPath: `${dpRoot}Fail Then Fail`,
		description: `System value for 'Fail Then Fail' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.FailThenFail',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumFailThenPass: {
		defaultValue: 'Fail Then Pass',
		displayPath: `${dpRoot}Fail Then Pass`,
		description: `System value for 'Fail Then Pass' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.FailThenPass',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumItemExposure: {
		defaultValue: 'Item Exposure',
		displayPath: `${dpRoot}Item Exposure`,
		description: `System value for 'Item Exposure' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.ItemExposure',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumNoViolation: {
		defaultValue: 'No Violation',
		displayPath: `${dpRoot}No Violation`,
		description: `System value for 'No Violation' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.NoViolation',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumNull: {
		defaultValue: 'Null',
		displayPath: `${dpRoot}Null`,
		description: `System value for 'Null' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.Null',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumOrder: {
		defaultValue: 'Order',
		displayPath: `${dpRoot}Order`,
		description: `System value for 'Order' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.Order',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumPassThenFail: {
		defaultValue: 'Pass Then Fail',
		displayPath: `${dpRoot}Pass Then Fail`,
		description: `System value for 'Pass Then Fail' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.PassThenFail',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumPassThenPass: {
		defaultValue: 'Pass Then Pass',
		displayPath: `${dpRoot}Pass Then Pass`,
		description: `System value for 'Pass Then Pass' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.PassThenPass',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumPoorPerformance: {
		defaultValue: 'Poor Performance',
		displayPath: `${dpRoot}Poor Performance`,
		description: `System value for 'Poor Performance' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.PoorPerformance',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumPsychometric: {
		defaultValue: 'Psychometric',
		displayPath: `${dpRoot}Psychometric`,
		description: `System value for 'Psychometric' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.Psychometric',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumRsi: {
		defaultValue: 'RSI',
		displayPath: `${dpRoot}RSI`,
		description: `System value for 'RSI' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.Rsi',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumScoreChange: {
		defaultValue: 'Score Change',
		displayPath: `${dpRoot}Score Change`,
		description: `System value for 'Score Change' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.ScoreChange',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumScoreTooHighTimeTooShort: {
		defaultValue: 'Score Too High Time Too Short',
		displayPath: `${dpRoot}Score Too High Time Too Short`,
		description: `System value for 'Score Too High Time Too Short' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.ScoreTooHighTimeTooShort',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumScoredUnscoredPerformance: {
		defaultValue: 'Scored Unscored Performance',
		displayPath: `${dpRoot}Scored Unscored Performance`,
		description: `System value for 'Scored Unscored Performance' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.ScoredUnscoredPerformance',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumTimeTooShort: {
		defaultValue: 'Time Too Short',
		displayPath: `${dpRoot}Time Too Short`,
		description: `System value for 'Time Too Short' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.TimeTooShort',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumTimeTooShortForTooManyItems: {
		defaultValue: 'Time Too Short For Too Many Items',
		displayPath: `${dpRoot}Time Too Short For Too Many Items`,
		description: `System value for 'Time Too Short For Too Many Items' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.TimeTooShortForTooManyItems',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumTotalExceeded: {
		defaultValue: 'Total Exceeded',
		displayPath: `${dpRoot}Total Exceeded`,
		description: `System value for 'Total Exceeded' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.TotalExceeded',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumTrojanItem: {
		defaultValue: 'Trojan Item',
		displayPath: `${dpRoot}Trojan Item`,
		description: `System value for 'Trojan Item' exam violation type`,
		legacyName: 'Exam.Security/System.Enum.ExamViolationType.TrojanItem',
		...sharedSpLocData,
	},
	SPExamViolationTypeEnumFaceRecognition: {
		defaultValue: 'Face Recognition',
		displayPath: `${dpRoot}Face Recognition`,
		description: `System value for 'Face Recognition' exam violation type`,
		...sharedSpLocData,
	},
};

export const EXAM_VIOLATION_TYPE_ENUM_LOCS = MapLocKeys<
	CPExamViolationTypeLocs | SPExamViolationTypeLocs
>({
	...cpExamViolationTypeEnumLocs,
	...spExamViolationTypeEnumLocs,
});

export type CPExamViolationTypeEnumLocType = LocMap<CPExamViolationTypeLocs>;
export type SPExamViolationTypeEnumLocType = LocMap<SPExamViolationTypeLocs>;

export const cpExamViolationTypeKeys: {
	[key in ExamViolationType]: keyof CPExamViolationTypeEnumLocType;
} = {
	[ExamViolationType.AdvancedSecurityRule]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumAdvancedSecurityRule,
	[ExamViolationType.CandidateRelated]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumCandidateRelated,
	[ExamViolationType.Collusion]: EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumCollusion,
	[ExamViolationType.DifferentialPerformance]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumDifferentialPerformance,
	[ExamViolationType.FailThenFail]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumFailThenFail,
	[ExamViolationType.FailThenPass]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumFailThenPass,
	[ExamViolationType.ItemExposure]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumItemExposure,
	[ExamViolationType.NoViolation]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumNoViolation,
	[ExamViolationType.Null]: EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumNull,
	[ExamViolationType.Order]: EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumOrder,
	[ExamViolationType.PassThenFail]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumPassThenFail,
	[ExamViolationType.PassThenPass]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumPassThenPass,
	[ExamViolationType.PoorPerformance]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumPoorPerformance,
	[ExamViolationType.Psychometric]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumPsychometric,
	[ExamViolationType.Rsi]: EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumRsi,
	[ExamViolationType.ScoreChange]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumScoreChange,
	[ExamViolationType.ScoreTooHighTimeTooShort]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumScoreTooHighTimeTooShort,
	[ExamViolationType.ScoredUnscoredPerformance]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumScoredUnscoredPerformance,
	[ExamViolationType.TimeTooShort]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumTimeTooShort,
	[ExamViolationType.TimeTooShortForTooManyItems]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumTimeTooShortForTooManyItems,
	[ExamViolationType.TotalExceeded]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumTotalExceeded,
	[ExamViolationType.TrojanItem]: EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumTrojanItem,
	[ExamViolationType.Voucher]: EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumVoucher,
	[ExamViolationType.FaceRecognition]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.CPExamViolationTypeEnumFaceRecognition,
};

export const spExamViolationTypeKeys: {
	[key in ExamViolationType]: keyof SPExamViolationTypeEnumLocType;
} = {
	[ExamViolationType.AdvancedSecurityRule]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumAdvancedSecurityRule,
	[ExamViolationType.CandidateRelated]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumCandidateRelated,
	[ExamViolationType.Collusion]: EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumCollusion,
	[ExamViolationType.DifferentialPerformance]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumDifferentialPerformance,
	[ExamViolationType.FailThenFail]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumFailThenFail,
	[ExamViolationType.FailThenPass]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumFailThenPass,
	[ExamViolationType.ItemExposure]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumItemExposure,
	[ExamViolationType.NoViolation]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumNoViolation,
	[ExamViolationType.Null]: EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumNull,
	[ExamViolationType.Order]: EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumOrder,
	[ExamViolationType.PassThenFail]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumPassThenFail,
	[ExamViolationType.PassThenPass]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumPassThenPass,
	[ExamViolationType.PoorPerformance]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumPoorPerformance,
	[ExamViolationType.Psychometric]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumPsychometric,
	[ExamViolationType.Rsi]: EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumRsi,
	[ExamViolationType.ScoreChange]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumScoreChange,
	[ExamViolationType.ScoreTooHighTimeTooShort]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumScoreTooHighTimeTooShort,
	[ExamViolationType.ScoredUnscoredPerformance]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumScoredUnscoredPerformance,
	[ExamViolationType.TimeTooShort]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumTimeTooShort,
	[ExamViolationType.TimeTooShortForTooManyItems]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumTimeTooShortForTooManyItems,
	[ExamViolationType.TotalExceeded]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumTotalExceeded,
	[ExamViolationType.TrojanItem]: EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumTrojanItem,
	[ExamViolationType.Voucher]: EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumVoucher,
	[ExamViolationType.FaceRecognition]:
		EXAM_VIOLATION_TYPE_ENUM_LOCS.SPExamViolationTypeEnumFaceRecognition,
};
